import { NavLink, Navigate, useLocation, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Icon from "@mui/material/Icon";
import Typography from "@mui/material/Typography";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import DashboardLayout from "../../../widgets/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../../widgets/Navbars/DashboardNavbar";
import { PageContentShell } from "../../../components/layout";
import { PageHeader } from "../../../components/Page";
import { useAppCustomStyles } from "../../../assets/theme/custom/hooks";
import {
  DOC_TOC_GROUPS,
  DEFAULT_DOC_SECTION,
  extractDocHeadings,
  isDocSection,
  type DocTocChild,
} from "../toc";
import { getDocMarkdown } from "../loadDoc";
import { useDocsMarkdownComponents } from "./markdownComponents";
import { USE_CASE_FLOW_TOC, UseCaseFlowsView } from "./useCaseFlows";

const INTERACTIVE_SECTION = "use-case-flows";

export default function DocsLayout() {
  const { t, i18n } = useTranslation();
  const { section } = useParams<{ section: string }>();
  const location = useLocation();
  const docs = useAppCustomStyles().pages.docs;
  const markdownComponents = useDocsMarkdownComponents();
  // Below md the table of contents folds away so the article starts on the first screen.
  const [tocOpen, setTocOpen] = useState(false);

  if (section && !isDocSection(section)) {
    return <Navigate to={`/docs/${DEFAULT_DOC_SECTION}`} replace />;
  }

  const activeSection = section && isDocSection(section) ? section : DEFAULT_DOC_SECTION;
  const isInteractive = activeSection === INTERACTIVE_SECTION;
  const markdown = isInteractive ? "" : getDocMarkdown(activeSection, i18n.language);
  const activeHash = location.hash.replace(/^#/, "");

  useEffect(() => {
    setTocOpen(false);
  }, [activeSection, activeHash]);

  useEffect(() => {
    if (!activeHash) return;
    const el = document.getElementById(activeHash);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [activeHash, activeSection, markdown, isInteractive]);

  const sectionChildren = (id: string): DocTocChild[] => {
    if (id !== activeSection) return [];
    if (id === INTERACTIVE_SECTION) {
      return USE_CASE_FLOW_TOC.map((item) => ({
        id: item.id,
        text: t(`docs.useCaseFlows.cases.${item.titleKey}`),
      }));
    }
    return extractDocHeadings(markdown);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <PageContentShell>
        <PageHeader title={t("docs.title")} subtitle={t("docs.subtitle")} />
        <Box sx={docs.shell}>
          <Box component="nav" sx={docs.toc} aria-label={t("docs.tocTitle")}>
            <Box
              component="button"
              type="button"
              aria-expanded={tocOpen}
              onClick={() => setTocOpen((open) => !open)}
              sx={{
                display: { xs: "flex", md: "none" },
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                minHeight: 44,
                p: 0,
                border: 0,
                bgcolor: "transparent",
                color: "text.primary",
                font: "inherit",
                fontWeight: 600,
                textAlign: "left",
                cursor: "pointer",
              }}
            >
              <span>
                {t("docs.tocTitle")} · {t(`docs.toc.${activeSection}`)}
              </span>
              <Icon>{tocOpen ? "expand_less" : "expand_more"}</Icon>
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={[docs.tocTitle, { display: { xs: "none", md: "block" } }] as any}
            >
              {t("docs.tocTitle")}
            </Typography>
            <Box
              sx={{
                display: { xs: tocOpen ? "flex" : "none", md: "flex" },
                flexDirection: "column",
                flex: 1,
                minHeight: 0,
                // Mobile accordion: cap height so a long TOC scrolls inside the panel.
                maxHeight: { xs: "60vh", md: "none" },
              }}
            >
              <Box sx={docs.tocList}>
                {DOC_TOC_GROUPS.map((group) => (
                  <Box key={group.id} sx={docs.tocGroup}>
                    <Typography component="div" sx={docs.tocGroupLabel}>
                      {t(`docs.groups.${group.labelKey}`)}
                    </Typography>
                    {group.sections.map((id) => {
                      const to = `/docs/${id}`;
                      const children = sectionChildren(id);
                      const sectionActive = activeSection === id;
                      return (
                        <Box key={id}>
                          <Box
                            component={NavLink}
                            to={to}
                            sx={[docs.tocLink, sectionActive ? docs.tocLinkActive : null]}
                          >
                            {t(`docs.toc.${id}`)}
                          </Box>
                          {sectionActive &&
                            children.map((child) => {
                              const childTo = `${to}#${child.id}`;
                              const childActive = activeHash === child.id;
                              return (
                                <Box
                                  key={child.id}
                                  component={NavLink}
                                  to={childTo}
                                  sx={[
                                    docs.tocLinkNested,
                                    childActive ? docs.tocLinkNestedActive : null,
                                  ]}
                                >
                                  {child.text}
                                </Box>
                              );
                            })}
                        </Box>
                      );
                    })}
                  </Box>
                ))}
                <Box sx={docs.tocGroup}>
                  <Box component={NavLink} to="/release-notes" sx={docs.tocLink}>
                    {t("docs.releaseNotesTitle")}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
          <Box component="article" sx={docs.article}>
            {isInteractive ? (
              <UseCaseFlowsView />
            ) : (
              <Box sx={docs.markdown}>
                <Markdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                  {markdown}
                </Markdown>
              </Box>
            )}
          </Box>
        </Box>
      </PageContentShell>
    </DashboardLayout>
  );
}
